import React from "react"
import ReactDOM from "react-dom/client"

import store from "./store/store"
import { Provider } from "react-redux"

import App from "./App"

import * as serviceWorker from "./serviceWorker"

import { createTheme, StyledEngineProvider, ThemeProvider } from "@mui/material/styles"

import { BrowserRouter } from "react-router-dom"

import { nlNL as coreNL } from "@mui/material/locale"

import "./index.css"

const theme = createTheme(
  {
    typography: {
      allVariants: {
        fontFamily: [
          "RijksoverheidSansText"
        ].join(","),
        textTransform: "none",
        lineHeight: 1.6
      },//"RijksoverheidSansText,Adjusted Arial Fallback,sans-serif",
      h1: { fontSize: "2.1rem", fontWeight: 400, lineHeight: 1.3 },
      h2: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.3 },
      h3: { fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.3 },
      h4: { fontSize: "1.075rem", fontWeight: 700, lineHeight: 1.3 },
      h5: { fontSize: "1rem", fontWeight: 700, lineHeight: 1.3 },
      h6: { fontSize: "0.92rem", fontWeight: 700, lineHeight: 1.3, textTransform: "uppercase" },
    },
    spacing: 8,
    breakpoints: {
      values: {
        xs: 0,
        sm: 650,
        md: 1000,
        lg: 1240,
        xl: 1536,
      },
    },
    palette: {
      lintblauw: { main: "#154273" },
      paars: { main: "#42145f" },
      violet: { main: "#a90061" },
      robijnrood: { main: "#ca005d" },
      roze: { main: "#f092cd" },
      rood: { main: "#d52b1e" },
      oranje: { main: "#e17000" },
      donkergeel: { main: "#ffb612" },
      geel: { main: "#f9e11e" },
      donkerbruin: { main: "#673327" },
      bruin: { main: "#94710a" },
      donkergroen: { main: "#275937" },
      groen: { main: "#39870c" },
      mosgroen: { main: "#777b00" },
      mintgroen: { main: "#76d2b6" },
      donkerblauw: { main: "#01689b" },
      hemelblauw: { main: "#007bc7" },
      lichtblauw: {
        light: "#BCDFF0", // 60%
        main: "#8fcae7"
      },
      text: {
        primary: "#222222",
        secondary: "#ffffff"
      },
      grey: {
        1: "#f3f3f3",
        2: "#e6e6e6",
        3: "#cccccc",
        4: "#b4b4b4",
        5: "#999999",
        6: "#696969",
        7: "#535353"
      },
      primary: {
        light: "#BCDFF0",
        main: "#007bc7",
        dark: "#01689b",
        contrastText: "#FFFFFF"
      },
      secondary: {
        light: "#F1F1F1",
        main: "#E8E8E8",
        dark: "#DDDDDD"
      }
    },
    components: {
      MuiDataGrid: {
        styleOverrides: {
          columnHeaders: {
            backgroundColor: "#007CC0",
            color: "#ffffff",
          },
          columnHeaderTitle: {
            color: "#ffffff",
            fontWeight: 600,
          },
          iconSeparator: {
            color: "rgba(255, 255, 255, 0.7)"
          },
          menuIconButton: {
            color: "rgba(255, 255, 255, 0.7)"
          },
          sortIcon: {
            color: "rgba(255, 255, 255, 0.9)"
          }
        }
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            margin: "8px 0 0 0",
            "&:before": {
              display: "none"
            },
            "&:first-of-type": {
              marginTop: 0
            },
            "&.Mui-expanded": {
              margin: "8px 0 0 0"
            }
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            // padding: "0 8px",
            minHeight: 48,
            height: 48,
            "&.Mui-expanded": {
              minHeight: 48,
              height: 48
            }
          },
          content: {
            margin: "auto 0 !important",
            alignItems: "center"
          },
          expandIconWrapper: {
            padding: 4,
            margin: "0 -8px 0 0"
          }
        }
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            display: "flex",
            flexDirection: "column",
            padding: "0 8px 8px"
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          overflow: "hidden"
        }
      },
      MuiSlider: {
        styleOverrides: {
          thumb: {
            "&:hover, &.Mui-focusVisible": {
              boxShadow: "0 0 0 6px rgba(0,124,192,0.16)"
            },
            "&.Mui-active": {
              boxShadow: "0 0 0 8px rgba(0,124,192,0.16)"
            }
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" },
              "&.Mui-focusVisible": { background: "rgba(0, 0, 0, 0.08)" }
            }
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        }
      },
      MuiCheckbox: {
        defaultProps: {
          disableFocusRipple: true,
          disableTouchRipple: true,
          disableRipple: true
        }
      }
    }
  },
  coreNL,
  // nlNL
)

const root = ReactDOM.createRoot(document.getElementById("root"))

root.render(
  <Provider store={store}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ThemeProvider>
    </StyledEngineProvider>
  </Provider>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
